import { useEffect, useRef, useState } from "react";
import { useErrorLog } from "./Toast.js";

function IconAlert({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function formatTimestamp(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  return new Date(iso).toLocaleString();
}

export function ErrorLogIndicator() {
  const { entries, clear, remove } = useErrorLog();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      if (!panelRef.current?.contains(t) && !buttonRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("click", onDocClick);
      return () => document.removeEventListener("click", onDocClick);
    }
    return undefined;
  }, [open]);

  if (entries.length === 0 && !open) return null;

  const count = entries.length;

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        className="menuButton"
        aria-label={`Recent errors${count > 0 ? ` (${count})` : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ position: "relative", color: count > 0 ? "var(--danger-text)" : undefined }}
      >
        <IconAlert size={18} />
        {count > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              borderRadius: 999,
              background: "var(--danger-text)",
              color: "white",
              textAlign: "center"
            }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Recent errors"
          className="menuPanel"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            width: 380,
            maxHeight: 420,
            display: "flex",
            flexDirection: "column",
            zIndex: 100,
          }}
        >
          <header
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 700 }}>Recent errors</div>
            <button
              type="button"
              className="btn btnSm"
              onClick={clear}
              disabled={count === 0}
            >
              Clear all
            </button>
          </header>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {count === 0 ? (
              <div className="muted" style={{ padding: 16, fontSize: 13 }}>
                No errors this session.
              </div>
            ) : (
              entries.map((e) => (
                <div
                  key={e.id}
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                    <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {e.message}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>{formatTimestamp(e.ts)}</div>
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss error"
                    title="Dismiss"
                    onClick={() => remove(e.id)}
                    style={{
                      flex: "0 0 auto",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "var(--danger-text)",
                      cursor: "pointer",
                      borderRadius: 4,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
